import classNames from "classnames";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useOrderSummaryContext } from "~/contexts/cart";
import { useCart } from "~/hooks/use-cart";
import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import CommonUtils from "~/utils/common-utils";

import CheckoutProductTile from "../checkout/checkout-product-tile";
import Button from "../common/button";
import Dialog from "../common/dialog";
import styles from "./stock-details-modal.module.scss";
import { ScrollGradient } from "./stores-selection";

type Props = {
  store?: AbstractShop;
  availableProducts?: EVA.Core.ProductAvailabilityResult[];
};

export default function StockDetailsModal({ store, availableProducts }: Props) {
  const t = useTranslations();
  const cart = useCart();
  const products = cart?.ShoppingCart.Lines.filter((el) => !!el.ProductID);
  const breakpoint = useBreakpoint();

  const storesList = useRef<HTMLUListElement>(null);
  const [listGradient, setListGradient] = useState<ScrollGradient>({ top: false, bot: false });
  const { openOrderSummary, setOpenOrderSummary, showStockDetails, openStockDetails } = useOrderSummaryContext();
  const enableCollectInStoreBanner = CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_CLICK_AND_COLLECT_REMOVE_PRODUCT);

  // this effect handles gradients on top/bottom of the scrollable container
  useEffect(() => {
    let currentList = storesList.current;

    const onScroll = () => {
      if (currentList) {
        if (currentList.scrollTop > 0) {
          // add gradient top
          setListGradient((curr) => ({ ...curr, top: true }));
        } else {
          // remove gradient top
          setListGradient((curr) => ({ ...curr, top: false }));
        }
        const diff = currentList.scrollHeight - currentList.offsetHeight;
        // threshold of 10px
        if (currentList.scrollTop >= diff - 10) {
          // remove gradient bottom
          setListGradient((curr) => ({ ...curr, bot: false }));
        } else {
          // add gradient bottom
          setListGradient((curr) => ({ ...curr, bot: true }));
        }
      }
    };

    let initInterval = setInterval(() => {
      if (storesList.current) {
        clearInterval(initInterval);
        currentList = storesList.current;
        if (currentList.scrollHeight > currentList.offsetHeight) {
          // add gradient - listener scroll
          onScroll();
          currentList.addEventListener("scroll", onScroll);
        } else {
          setListGradient({ top: false, bot: false });
        }
      }
    }, 100);

    return () => currentList?.removeEventListener("scroll", onScroll);
  }, [products]);

  return (
    (!openOrderSummary || breakpoint === "mobile") && (
      <>
        <button
          type="button"
          onClick={() => {
            if (enableCollectInStoreBanner && breakpoint !== "mobile") {
              setOpenOrderSummary(true);
            } else {
              openStockDetails(true);
            }
          }}
          className={styles.button}
        >
          {t("generic.viewDetails")}
        </button>
        <Dialog
          isOpen={showStockDetails}
          title={
            <span>
              {t("generic.your_products")} ({products?.length})
            </span>
          }
          onClose={() => openStockDetails(false)}
          customStyles={{
            wrapper: styles.modalWrapper,
            panel: styles.modalPanel,
            content: styles.modalContent,
          }}
        >
          <div
            className={classNames(
              styles.relativeContainer,
              { [styles.topGradient]: listGradient.top },
              { [styles.botGradient]: listGradient.bot }
            )}
          >
            <ul ref={storesList} className={styles.innerContainer}>
              {products?.map((el) => {
                return (
                  <div key={el.ID}>
                    <CheckoutProductTile line={el} hideQty store={store} />
                  </div>
                );
              })}
            </ul>
          </div>
          <Button variant="primary" onClick={() => openStockDetails(false)}>
            {t(store ? "generic.backToStoreSelection" : "generic.backToShipSelection")}
          </Button>
        </Dialog>
      </>
    )
  );
}
